import queryString from "query-string"
import { useCallback, useEffect, useMemo, useState } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import {
  ALL_SPECIES,
  ANIMAL_GENDERS,
  ANIMAL_STATUS_ENUM,
  type AnimalLifeSpan,
} from "@/features/farm"
import { useGetAnimalsQuery } from "@/features/farm/api/animals-api.ts"
import { FILTER_AGE } from "@/features/farm/types/animal-ages-constants.ts"
import {
  ANIMAL_FILTER_ENUM,
  filterAnimals,
  type AnimalFilterSelection,
} from "@/features/farm/utils/animal-filter.ts"

const useAnimalFilterURL = () => {
  const navigate = useNavigate()
  const { data = [], isLoading } = useGetAnimalsQuery()

  const query = new URLSearchParams(useLocation().search)
  const searchQuery = query.get(ANIMAL_FILTER_ENUM.SEARCH_QUERY) || ""
  const status =
    query.get(ANIMAL_FILTER_ENUM.STATUS) || ANIMAL_STATUS_ENUM.ALL_STATUSES
  const gender =
    query.get(ANIMAL_FILTER_ENUM.GENDER) || ANIMAL_GENDERS.ALL_GENDERS.label
  const species = query.get(ANIMAL_FILTER_ENUM.SPECIES) || ALL_SPECIES.label
  const ageString = query.get(ANIMAL_FILTER_ENUM.AGE)
  const outOfExploitation =
    query.get(ANIMAL_FILTER_ENUM.OUT_OF_EXPLOITATION) === "true"
  const age: AnimalLifeSpan = ageString
    ? (JSON.parse(ageString) as AnimalLifeSpan)
    : FILTER_AGE

  const initialFilter: AnimalFilterSelection = {
    searchQuery,
    status,
    species,
    age,
    gender,
    outOfExploitation,
  }

  const [selections, setSelections] = useState(initialFilter)

  const updateURL = useCallback(() => {
    const queryParams: AnimalFilterSelection = {
      searchQuery: selections.searchQuery,
      status: selections.status,
      species: selections.species,
      age: selections.age,
      gender: selections.gender,
      outOfExploitation: selections.outOfExploitation,
    }
    const searchString = queryString.stringify({
      ...queryParams,
      age: JSON.stringify(selections.age),
    })
    navigate({ search: searchString }, { replace: true })
  }, [selections, navigate])

  useEffect(() => {
    updateURL()
  }, [selections, updateURL])

  const handleAgeSelection = (age: AnimalLifeSpan) => {
    setSelections((prev) => ({
      ...prev,
      age,
    }))
  }

  const handleGenderSelection = (gender: string) => {
    setSelections((prev) => ({
      ...prev,
      gender,
    }))
  }

  const handleSpeciesSelection = (species: string) => {
    setSelections((prev) => ({
      ...prev,
      species,
    }))
  }

  const handleSearchQuery = (searchQuery: string) => {
    setSelections((prev) => ({
      ...prev,
      searchQuery,
    }))
  }

  const handleStatusSelection = (status: string) => {
    setSelections((prev) => ({
      ...prev,
      status,
    }))
  }

  const handleOutOfExploitationSelection = (outOfExploitation: boolean) => {
    setSelections((prev) => ({
      ...prev,
      outOfExploitation,
    }))
  }

  const animals = useMemo(
    () => filterAnimals(data, selections),
    [data, selections],
  )

  return {
    selections,
    animals,
    isLoading,
    handleAgeSelection,
    handleGenderSelection,
    handleSpeciesSelection,
    handleSearchQuery,
    handleStatusSelection,
    handleOutOfExploitationSelection,
  }
}

export default useAnimalFilterURL
